import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { jobsApi, formatApiError } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { 
    ArrowLeft, Plus, X, Save, Loader2
} from 'lucide-react';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../../components/ui/select';
import { toast } from 'sonner';

export default function JobFormPage() {
    const { t } = useTranslation();
    const { id } = useParams();
    const navigate = useNavigate();
    const isEdit = !!id;
    
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [formData, setFormData] = useState({
        title: '',
        description: '',
        department: '',
        location: '',
        employment_type: 'full-time',
        experience_min: 0,
        experience_max: 10,
        salary_min: '',
        salary_max: '',
        skills_required: []
    });
    const [newSkill, setNewSkill] = useState({ name: '', level: 'mid', weight: 3 });

    useEffect(() => {
        if (isEdit) {
            loadJob();
        }
    }, [id]);

    const loadJob = async () => {
        setLoading(true);
        try {
            const response = await jobsApi.getById(id);
            setFormData({
                ...response.data,
                salary_min: response.data.salary_min || '',
                salary_max: response.data.salary_max || ''
            });
        } catch (error) {
            toast.error(t('errors.generic_load'));
            navigate('/dashboard/jobs');
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (field, value) => {
        setFormData({ ...formData, [field]: value });
    };

    const addSkill = () => {
        if (!newSkill.name.trim()) return;
        setFormData({
            ...formData,
            skills_required: [...formData.skills_required, { ...newSkill }]
        });
        setNewSkill({ name: '', level: 'mid', weight: 3 });
    };

    const removeSkill = (index) => {
        setFormData({
            ...formData,
            skills_required: formData.skills_required.filter((_, i) => i !== index)
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formData.title.trim() || !formData.description.trim()) {
            toast.error(t('jobs.form_required_missing'));
            return;
        }

        setSaving(true);
        try {
            const payload = {
                ...formData,
                salary_min: formData.salary_min ? parseFloat(formData.salary_min) : null,
                salary_max: formData.salary_max ? parseFloat(formData.salary_max) : null
            };

            if (isEdit) {
                await jobsApi.update(id, payload);
                toast.success(t('jobs.form_updated'));
                navigate(`/dashboard/jobs/${id}`);
            } else {
                await jobsApi.create(payload);
                toast.success(t('jobs.form_created'));
                navigate('/dashboard/jobs');
            }
        } catch (error) {
            toast.error(formatApiError(error));
        } finally {
            setSaving(false);
        }
    };

    const getLevelLabel = (level) => {
        switch (level) {
            case 'junior': return t('jobs.form_level_junior');
            case 'mid': return t('jobs.form_level_mid');
            case 'senior': return t('jobs.form_level_senior');
            case 'expert': return t('jobs.form_level_expert');
            default: return level;
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <Loader2 className="w-8 h-8 animate-spin text-[#002FA7]" />
            </div>
        );
    }

    return (
        <div className="max-w-3xl mx-auto" data-testid="job-form-page">
            {/* Header */}
            <div className="flex items-center gap-4 mb-8">
                <button 
                    onClick={() => navigate(isEdit ? `/dashboard/jobs/${id}` : '/dashboard/jobs')}
                    className="p-2 hover:bg-[#F3F4F6] rounded-lg transition-colors"
                >
                    <ArrowLeft className="w-5 h-5 text-[#4B5563]" />
                </button>
                <div>
                    <h1 className="text-2xl font-bold text-[#0A0A0A] font-['Outfit']">
                        {isEdit ? t('jobs.form_edit_title') : t('jobs.form_new_title')}
                    </h1>
                    <p className="text-[#4B5563]">
                        {isEdit ? t('jobs.form_edit_subtitle') : t('jobs.form_new_subtitle')}
                    </p>
                </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-8">
                {/* Basic info */}
                <div className="bg-white p-6 rounded-lg border border-[#E5E7EB] space-y-6">
                    <h2 className="text-lg font-semibold text-[#0A0A0A] font-['Outfit']">
                        {t('jobs.form_general_info')}
                    </h2>

                    <div className="space-y-2">
                        <Label htmlFor="title">{t('jobs.form_title_label')} *</Label>
                        <Input
                            id="title"
                            value={formData.title}
                            onChange={(e) => handleChange('title', e.target.value)}
                            placeholder={t('jobs.form_title_placeholder')}
                            required
                            data-testid="job-title-input"
                        />
                    </div>

                    <div className="space-y-2">
                        <Label htmlFor="description">{t('jobs.form_description_label')} *</Label>
                        <Textarea
                            id="description"
                            value={formData.description}
                            onChange={(e) => handleChange('description', e.target.value)}
                            placeholder={t('jobs.form_description_placeholder')}
                            rows={6}
                            required
                            data-testid="job-description-input"
                        />
                    </div>

                    <div className="grid grid-cols-1 gap-4">
                        <div className="space-y-2">
                            <Label htmlFor="location">{t('jobs.form_location_label')}</Label>
                            <Input
                                id="location"
                                value={formData.location}
                                onChange={(e) => handleChange('location', e.target.value)}
                                placeholder={t('jobs.form_location_placeholder')}
                            />
                        </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="space-y-2">
                            <Label>{t('jobs.form_employment_label')}</Label>
                            <Select 
                                value={formData.employment_type} 
                                onValueChange={(v) => handleChange('employment_type', v)}
                            >
                                <SelectTrigger>
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="full-time">{t('jobs.employment_full_time')}</SelectItem>
                                    <SelectItem value="part-time">{t('jobs.employment_part_time')}</SelectItem>
                                    <SelectItem value="contract">{t('jobs.employment_contract')}</SelectItem>
                                    <SelectItem value="remote">{t('jobs.employment_remote')}</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                </div>

                {/* Experience */}
                <div className="bg-white p-6 rounded-lg border border-[#E5E7EB] space-y-6">
                    <h2 className="text-lg font-semibold text-[#0A0A0A] font-['Outfit']">
                        {t('jobs.form_experience_title')}
                    </h2>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="space-y-2">
                            <Label htmlFor="experience_min">{t('jobs.form_exp_min_label')}</Label>
                            <Input
                                id="experience_min"
                                type="number"
                                min="0"
                                value={formData.experience_min}
                                onChange={(e) => handleChange('experience_min', parseInt(e.target.value) || 0)}
                            />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="experience_max">{t('jobs.form_exp_max_label')}</Label>
                            <Input
                                id="experience_max"
                                type="number"
                                min="0"
                                value={formData.experience_max}
                                onChange={(e) => handleChange('experience_max', parseInt(e.target.value) || 0)}
                            />
                        </div>
                    </div>
                </div>

                {/* Salary */}
                <div className="bg-white p-6 rounded-lg border border-[#E5E7EB] space-y-6">
                    <h2 className="text-lg font-semibold text-[#0A0A0A] font-['Outfit']">
                        {t('jobs.form_salary_title')}
                    </h2>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="space-y-2">
                            <Label htmlFor="salary_min">{t('jobs.form_salary_min_label')}</Label>
                            <Input
                                id="salary_min"
                                type="number"
                                min="0"
                                value={formData.salary_min}
                                onChange={(e) => handleChange('salary_min', e.target.value)}
                                placeholder={t('jobs.form_salary_min_placeholder')}
                            />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="salary_max">{t('jobs.form_salary_max_label')}</Label>
                            <Input
                                id="salary_max"
                                type="number"
                                min="0"
                                value={formData.salary_max}
                                onChange={(e) => handleChange('salary_max', e.target.value)}
                                placeholder={t('jobs.form_salary_max_placeholder')}
                            />
                        </div>
                    </div>
                </div>

                {/* Skills */}
                <div className="bg-white p-6 rounded-lg border border-[#E5E7EB] space-y-6">
                    <h2 className="text-lg font-semibold text-[#0A0A0A] font-['Outfit']">
                        {t('jobs.form_skills_title')}
                    </h2>

                    {/* Skills list */}
                    {formData.skills_required.length > 0 && (
                        <div className="flex flex-wrap gap-2">
                            {formData.skills_required.map((skill, index) => (
                                <div 
                                    key={index}
                                    className="flex items-center gap-2 px-3 py-2 bg-[#F3F4F6] rounded-lg"
                                >
                                    <span className="text-[#0A0A0A]">{skill.name}</span>
                                    <span className="text-xs text-[#9CA3AF]">
                                        {getLevelLabel(skill.level)}
                                    </span>
                                    <span className="text-xs text-[#002FA7] font-medium">
                                        ×{skill.weight}
                                    </span>
                                    <button
                                        type="button"
                                        onClick={() => removeSkill(index)}
                                        className="text-[#9CA3AF] hover:text-[#EF4444]"
                                    >
                                        <X className="w-4 h-4" />
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Add skill */}
                    <div className="flex flex-col sm:flex-row gap-3">
                        <Input
                            placeholder={t('jobs.form_skill_name_placeholder')}
                            value={newSkill.name}
                            onChange={(e) => setNewSkill({ ...newSkill, name: e.target.value })}
                            className="flex-1"
                            data-testid="skill-name-input"
                        />
                        <Select 
                            value={newSkill.level} 
                            onValueChange={(v) => setNewSkill({ ...newSkill, level: v })}
                        >
                            <SelectTrigger className="w-[140px]">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="junior">{t('jobs.form_level_junior')}</SelectItem>
                                <SelectItem value="mid">{t('jobs.form_level_mid')}</SelectItem>
                                <SelectItem value="senior">{t('jobs.form_level_senior')}</SelectItem>
                                <SelectItem value="expert">{t('jobs.form_level_expert')}</SelectItem>
                            </SelectContent>
                        </Select>
                        <Select 
                            value={String(newSkill.weight)} 
                            onValueChange={(v) => setNewSkill({ ...newSkill, weight: parseInt(v) })}
                        >
                            <SelectTrigger className="w-[100px]">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="1">×1</SelectItem>
                                <SelectItem value="2">×2</SelectItem>
                                <SelectItem value="3">×3</SelectItem>
                                <SelectItem value="4">×4</SelectItem>
                                <SelectItem value="5">×5</SelectItem>
                            </SelectContent>
                        </Select>
                        <Button 
                            type="button" 
                            variant="outline" 
                            onClick={addSkill}
                            data-testid="add-skill-btn"
                        >
                            <Plus className="w-4 h-4" />
                        </Button>
                    </div>
                    <p className="text-sm text-[#9CA3AF]">
                        {t('jobs.form_skill_weight_hint')}
                    </p>
                </div>

                {/* Actions */}
                <div className="flex justify-end gap-4">
                    <Button 
                        type="button" 
                        variant="outline"
                        onClick={() => navigate(isEdit ? `/dashboard/jobs/${id}` : '/dashboard/jobs')}
                    >
                        {t('common.cancel')}
                    </Button>
                    <Button 
                        type="submit" 
                        className="bg-[#002FA7] hover:bg-[#002480] text-white"
                        disabled={saving}
                        data-testid="submit-job-btn"
                    >
                        {saving ? (
                            <>
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                {t('common.saving')}
                            </>
                        ) : (
                            <>
                                <Save className="w-4 h-4 mr-2" />
                                {isEdit ? t('jobs.form_update') : t('jobs.form_create')}
                            </>
                        )}
                    </Button>
                </div>
            </form>
        </div>
    );
}
